import { getRunWorkerConfigFromWorkflowConfiguration } from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskDetailDrawer.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  useGetOneWorker,
  useGetOneWorkerRun,
} from "#src/components/hooks/adapters/useWorkers";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import {
  Accordion,
  Banner,
  EmptyState,
  KeyValueList,
  type KeyValuePairList,
} from "@validereinc/common-components";
import {
  WorkerRunStatus,
  type WorkerRunType,
  type WorkflowTaskType,
  type WorkflowType,
} from "@validereinc/domain";
import React, { useMemo } from "react";
import { recordToKeyValuePairs } from "../../WorkflowConfigurationDrawer/Utils/WorkflowConfigurationRunWorker.helpers";

const { AccordionPanel } = Accordion;

type WorkflowActionRunWorkerProps = {
  workflowTask: WorkflowTaskType;
  workflowConfiguration?: WorkflowType["config"];
  isLoading?: boolean;
};

/**
 * Displays run worker system action details inside the workflow task detail drawer.
 * Shows worker name, version, resolved input, output, and error message depending
 * on the worker run's status.
 */
export const WorkflowActionRunWorker = ({
  workflowTask,
  workflowConfiguration,
  isLoading: isExternalLoading,
}: WorkflowActionRunWorkerProps) => {
  const runWorkerConfig = getRunWorkerConfigFromWorkflowConfiguration(
    workflowTask.step_id,
    workflowConfiguration
  );

  const workerRunId = (workflowTask.metadata as Record<string, unknown>)
    ?.worker_run_id;
  const workerRunQuery = useGetOneWorkerRun(
    { id: String(workerRunId ?? "") },
    { enabled: Boolean(workerRunId) }
  );
  const workerRun: WorkerRunType | undefined = workerRunQuery.data?.data;

  const resolvedWorkerId =
    runWorkerConfig?.worker_id ?? workerRun?.worker_id ?? "";
  const workerQuery = useGetOneWorker(
    { id: resolvedWorkerId },
    { enabled: Boolean(resolvedWorkerId) }
  );
  const workerName = workerQuery.data?.data?.name;

  const versionNumber =
    runWorkerConfig?.worker_version_id ?? workerRun?.version ?? 1;

  const isLoading =
    isExternalLoading ||
    (Boolean(resolvedWorkerId) && workerQuery.isLoading) ||
    (Boolean(workerRunId) && workerRunQuery.isLoading);

  const workerOverviewData: KeyValuePairList = useMemo(() => {
    const data: KeyValuePairList = [];

    const workerDisplayLabel = workerName ?? resolvedWorkerId;
    data.push({
      title: "Worker Name",
      value: resolvedWorkerId ? (
        <RoutingLink
          to={WorkerDetailRoutePath.toLinkParts({
            pathParams: { workerId: resolvedWorkerId },
          })}
        >
          {workerDisplayLabel}
        </RoutingLink>
      ) : (
        "-"
      ),
    });

    const versionDisplay = String(versionNumber);

    data.push({
      title: "Version",
      value: resolvedWorkerId ? (
        <RoutingLink
          to={WorkerDetailRoutePath.toLinkParts({
            pathParams: {
              workerId: resolvedWorkerId,
            },
            queryParams: { version: versionDisplay },
          })}
        >
          {versionDisplay}
        </RoutingLink>
      ) : (
        versionDisplay
      ),
    });

    return data;
  }, [resolvedWorkerId, versionNumber, workerName]);

  const inputData: KeyValuePairList = useMemo(() => {
    if (workerRun?.inputs && Object.keys(workerRun.inputs).length > 0) {
      return recordToKeyValuePairs(workerRun.inputs);
    }

    if (
      runWorkerConfig?.worker_run_input &&
      Object.keys(runWorkerConfig.worker_run_input).length > 0
    ) {
      return recordToKeyValuePairs(runWorkerConfig.worker_run_input);
    }

    return [];
  }, [workerRun?.inputs, runWorkerConfig?.worker_run_input]);

  const outputData: KeyValuePairList = useMemo(() => {
    if (
      workerRun?.status === WorkerRunStatus.COMPLETED &&
      workerRun.outputs &&
      Object.keys(workerRun.outputs).length > 0
    ) {
      return recordToKeyValuePairs(workerRun.outputs);
    }

    return [];
  }, [workerRun?.status, workerRun?.outputs]);

  const isRunCompleted = workerRun?.status === WorkerRunStatus.COMPLETED;
  const isRunFailed = workerRun?.status === WorkerRunStatus.FAILED;
  const errorMessage = workerRun?.error_message;

  return (
    <>
      {isRunFailed && errorMessage ? (
        <Banner
          titleText="Worker run failed"
          description={errorMessage}
          variant="error"
        />
      ) : null}

      <Accordion
        defaultActiveKeys={["worker-overview", "worker-input", "worker-output"]}
      >
        <AccordionPanel
          dataKey="worker-overview"
          title="Worker Overview"
        >
          <KeyValueList
            data={workerOverviewData}
            variant="shaded"
            isLoading={isLoading}
          />
        </AccordionPanel>

        <AccordionPanel
          dataKey="worker-input"
          title="Input"
        >
          {inputData.length > 0 ? (
            <KeyValueList
              data={inputData}
              variant="shaded"
              isLoading={isLoading}
            />
          ) : (
            <EmptyState title="No input parameters." />
          )}
        </AccordionPanel>

        <AccordionPanel
          dataKey="worker-output"
          title="Output"
        >
          {isRunCompleted && outputData.length > 0 ? (
            <KeyValueList
              data={outputData}
              variant="shaded"
              isLoading={isLoading}
            />
          ) : (
            <EmptyState
              title={
                isRunCompleted
                  ? "No output available."
                  : "Output will be available when the worker completes."
              }
            />
          )}
        </AccordionPanel>
      </Accordion>
    </>
  );
};
